// external
import React, { FC } from 'react';
import { IconButton, Typography, Collapse, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import { format, parseISO } from 'date-fns';

// internal
import './coe-training-template-card.component.css';
import { formatDuration, getMeasurementUnit } from '../../helpers/general.helpers';
import COEEllipsisPopover from '../general/coe-ellipsis-popover/coe-ellipsis-popover.component';
import { palette } from '../../theme';

export interface TrainingTemplate {
  id: string;
  training_id: string;
  creator_id: string;
  title: string;
  description: string;
  time: string;
  location: string;
  sport: string;
  sport_type: string;
  focus: string;
  duration: number;
  distance: number;
}

export interface PopoverAction {
  title: string;
  action: () => void;
  color: 'inherit' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  hide?: boolean;
}

interface COETrainingTemplateCardProps {
  template: TrainingTemplate;
  expanded: boolean;
  onClick: () => void;
  actionType: 'add' | 'remove';
  onActionClick: (e: React.MouseEvent) => void;
  showExpandedDetails?: boolean;
  headerActions?: PopoverAction[];
  showAddToPlanButton?: boolean;
}

const SPORT_BADGE_COLORS: Record<string, { bg: string; text: string }> = {
  Running: { bg: '#DBEAFE', text: '#2563EB' },
  Cycling: { bg: '#DCFCE7', text: '#16A34A' },
  Swimming: { bg: '#E0F2FE', text: '#0284C7' },
  Skiing: { bg: '#F3EDFF', text: '#7C3AED' },
  Strength: { bg: '#FEF3C7', text: '#D97706' },
  Other: { bg: '#F3F4F6', text: '#6B7084' },
};

const formatDurationAsHours = (seconds: number): string => {
  const formatted = formatDuration(seconds.toString());
  const [hours, minutes] = formatted.split(':');
  const hoursNum = parseInt(hours, 10);
  const minutesNum = parseInt(minutes, 10);
  return `${hoursNum}:${minutesNum.toString().padStart(2, '0')} hr`;
};

const formatTime = (time: string): string => {
  try {
    if (time.includes('T') || time.includes('-')) {
      return format(parseISO(time), 'h:mm a');
    }
    return format(new Date(`2000-01-01T${time}`), 'h:mm a');
  } catch {
    return time;
  }
};

const COETrainingTemplateCard: FC<COETrainingTemplateCardProps> = (props) => {
  const {
    template,
    expanded,
    onClick,
    actionType,
    onActionClick,
    showExpandedDetails = true,
    headerActions,
    showAddToPlanButton = false,
  } = props;

  const sportColors = SPORT_BADGE_COLORS[template.sport] || SPORT_BADGE_COLORS.Other;
  const sportLabel = [template.sport, template.sport_type].filter(Boolean).join(' · ');

  return (
    <div className={`training-library-item${expanded ? ' training-library-item-open' : ''}`} onClick={onClick}>
      <div className="training-library-item-body">
        <div className="training-library-item-header">
          <span
            className="training-library-sport-badge"
            style={{ background: sportColors.bg, color: sportColors.text }}
          >
            {sportLabel || 'Training'}
          </span>
          <div className="training-library-item-header-right">
            <Typography variant="body2" className="training-library-item-time">
              {formatTime(template.time)}
            </Typography>
            {headerActions && headerActions.length > 0 && (
              <div className="training-library-ellipsis-wrapper" onClick={(e) => e.stopPropagation()}>
                <COEEllipsisPopover actions={headerActions} />
              </div>
            )}
            {!showAddToPlanButton && (
              <IconButton onClick={onActionClick} className="training-library-add-icon" size="small">
                {actionType === 'add' ? (
                  <AddIcon style={{ color: palette.primary.main }} />
                ) : (
                  <RemoveIcon style={{ color: palette.primary.main }} />
                )}
              </IconButton>
            )}
          </div>
        </div>

        <Typography variant="body2" className="training-library-item-title">
          {template.title}
        </Typography>

        <div className="training-library-item-footer">
          {template.location ? (
            <Typography variant="body2" className="training-library-item-location">
              <LocationOnOutlinedIcon sx={{ fontSize: 12 }} />
              {template.location}
            </Typography>
          ) : null}
          <div className="training-library-item-footer-right">
            <Typography variant="body2" className="training-library-item-duration">
              <AccessTimeOutlinedIcon sx={{ fontSize: 12 }} />
              {formatDurationAsHours(template.duration)}
            </Typography>
            <Typography variant="body2" className="training-library-item-distance">
              {template.distance} {getMeasurementUnit()}
            </Typography>
          </div>
        </div>
      </div>

      {showExpandedDetails && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <div className="training-library-item-expanded">
            <div className="training-library-expanded-row">
              <Typography
                variant="body2"
                className="training-library-expanded-label training-library-expanded-label-text"
              >
                Focus
              </Typography>
              <Typography variant="body2" className="training-library-expanded-value">
                {template.focus || '—'}
              </Typography>
            </div>
            <div className="training-library-expanded-row">
              <Typography
                variant="body2"
                className="training-library-expanded-label training-library-expanded-label-text"
              >
                Description
              </Typography>
              <Typography variant="body2" className="training-library-expanded-value">
                {template.description || '—'}
              </Typography>
            </div>
            {showAddToPlanButton ? (
              <Button
                className="training-library-add-to-plan-btn"
                fullWidth
                onClick={onActionClick}
                startIcon={<AddIcon />}
              >
                Add to Plan
              </Button>
            ) : null}
          </div>
        </Collapse>
      )}
    </div>
  );
};

export default COETrainingTemplateCard;
